import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { QualificationPromptPlaceholders } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import AiSearchBriefing from './ai-search-briefing';

interface Props {
  open: boolean;
  loading: boolean;
  step: number;
  productGroup: ProductGroups;

  promptPlaceholders: QualificationPromptPlaceholders;
  prompt: string;
  selectedOptions: string[];

  handleNextQualifyContactsStep(): void;
  handlePrevQualifyContactsStep(): void;

  handleAiBriefingDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAiBriefingDetailsChangeForLeadBooster(event: React.ChangeEvent<HTMLInputElement>): void;
  handleSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;
  handleCloseAndProceed(): void;
  startQualifyingContacts(): void;
}

const QualifyContactsDialog: React.FC<Props> = ({
  open,
  loading,
  step,
  promptPlaceholders,
  prompt,
  selectedOptions,
  handleNextQualifyContactsStep,
  handlePrevQualifyContactsStep,
  handleAiBriefingDetailsChange,
  handleAiBriefingDetailsChangeForLeadBooster,
  handleSelectedOptionChange,
  handleClose,
  handleCloseAndProceed,
  startQualifyingContacts,
  productGroup,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getStepContent = () => {
    if (step === 0) {
      return (
        <Stack>
          <Typography className={classes.blueTitle}>{getLocalizedText('manageContactsTitle', productGroup, t)}</Typography>
          <Typography className={classes.bigTitle}>{getLocalizedText('thisIsHowItGoes', productGroup, t)}</Typography>
          <Typography className={classes.greyText} mb={'24px'}>
            {getLocalizedText('qualifyCandidatesIntro', productGroup, t)}
          </Typography>
          <Typography className={classes.stepTitle} mb={'8px'}>
            {getLocalizedText('qualifyCandidatesStep1Title', productGroup, t)}
          </Typography>
          <Typography className={classes.stepText} mb={'24px'}>
            {getLocalizedText('qualifyCandidatesStep1Description', productGroup, t)}
          </Typography>
          <Typography className={classes.stepTitle} mb={'8px'}>
            {getLocalizedText('qualifyCandidatesStep2Title', productGroup, t)}
          </Typography>
          <Typography className={classes.stepText} mb={'24px'}>
            {getLocalizedText('qualifyCandidatesStep2Description', productGroup, t)}
          </Typography>
          <Typography className={classes.stepTitle} mb={'8px'}>
            {getLocalizedText('qualifyCandidatesStep3Title', productGroup, t)}
          </Typography>
          <Typography className={classes.stepText}>{getLocalizedText('qualifyCandidatesStep3Description', productGroup, t)}</Typography>
        </Stack>
      );
    }

    return (
      <AiSearchBriefing
        productGroup={productGroup}
        promptPlaceholders={promptPlaceholders}
        prompt={prompt}
        selectedOptions={selectedOptions}
        handleAiBriefingDetailsChange={handleAiBriefingDetailsChange}
        handleAiBriefingDetailsChangeForLeadBooster={handleAiBriefingDetailsChangeForLeadBooster}
        handleSelectedOptionChange={handleSelectedOptionChange}
      />
    );
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Stack direction={'row'} justifyContent={'space-between'} sx={{ position: 'relative' }}>
          <Typography className={classes.titleText}>
            {step === 0 ? getLocalizedText('qualifyContactsTitle', productGroup, t) : getLocalizedText('assessContactsTitle', productGroup, t)}
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ position: 'absolute', right: -10, top: -10 }}>
            <CloseIcon fontSize="small" className={classes.closeButton} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.content}>{getStepContent()}</DialogContent>

      <DialogActions className={classes.actions}>
        {step === 0 ? (
          <>
            <PaiButton
              text={t('dontQualify')}
              size={'small'}
              border={'rounded'}
              color={'secondary'}
              action={handleCloseAndProceed}
            />
            <PaiButton text={t('next')} size={'small'} border={'rounded'} action={handleNextQualifyContactsStep} />
          </>
        ) : (
          <>
            <PaiLink text={t('back')} size={'lg'} underline={'always'} action={handlePrevQualifyContactsStep} />
            <PaiButton
              text={getLocalizedText('startQualifying', productGroup, t)}
              size={'small'}
              border={'rounded'}
              loading={loading}
              disabled={(productGroup === ProductGroups.LEAD_BOOSTER && prompt === '') || _.isEmpty(selectedOptions)}
              action={startQualifyingContacts}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px 32px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '0 24px 20px 24px',
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  bigTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  infoContainer: {
    flexBasis: '50%',
  },
  image: {
    flexBasis: '50%',
    height: '225px',
    marginTop: '-24px',
  },
  exampleImage: {
    width: '100%',
    height: 'auto',
    filter: 'grayscale(100%)',
  },
  stepTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  stepText: {
    fontSize: theme.typography.pxToRem(15),
  },
  closeButton: {
    color: '#FFF',
  },
}));

export default QualifyContactsDialog;
